import { get } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe, type SafeString } from '@ember/template';
import Component from '@glimmer/component';
import { PrismTooltipHeader } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import type { SegmentationKey } from 'lumen/models/parity/insight/segmentation-item';
import type {
  Breakdown,
  ParityFilter,
} from 'lumen/utils/constants/pi-constant-values';
import {
  PARITY_FILTER,
  PARITY_FILTER_TRANSLATION_KEYS,
  SORT_TYPES,
} from 'lumen/utils/constants/pi-constant-values';
import roundParityPercentages from 'lumen/utils/parity-analytics/round-parity-percentages';

const PARITY_SHOPS_TRANSLATION_KEYS = {
  [PARITY_FILTER.WIN]: 'parity_analytics.report_win_shops',
  [PARITY_FILTER.MEET]: 'parity_analytics.report_meet_shops',
  [PARITY_FILTER.LOSS]: 'parity_analytics.report_loss_shops',
} as const;

type CalculatedValues = {
  [PARITY_FILTER.WIN]: number;
  [PARITY_FILTER.MEET]: number;
  [PARITY_FILTER.LOSS]: number;
  totalNbOfShops: number;
};

interface Violator {
  backgroundColor: SafeString;
  borderColor: SafeString;
  formattedNumberOfShops: string;
  formattedPercentage: string;
  formattedTotal: string;
  name: string;
  numberOfShops: number;
  selected: boolean;
  sortValue: number;
  total: number;
}

export interface ViolatorData {
  color: string;
  data: {
    calculatedValues: CalculatedValues;
    segmentationId: SegmentationKey;
    segmentationValue: string;
    value: {
      [PARITY_FILTER.WIN]: number;
      [PARITY_FILTER.MEET]: number;
      [PARITY_FILTER.LOSS]: number;
      subscriptions: number;
    };
  };
  name: string;
  selected: boolean;
}

interface ParityTooltipsTooltipTrendsSignature {
  Args: {
    displayName: string;
    key: ParityFilter;
    segmentation: Breakdown | null;
    sortType: string;
    violatorData: ViolatorData[];
  };
}

export default class ParityTooltipsTooltipTrends extends Component<ParityTooltipsTooltipTrendsSignature> {
  @service declare intl: Services['intl'];

  get segmentationName(): string {
    return this.intl.t(`parity_analytics.${this.args.segmentation}`, {
      count: 1,
    });
  }

  get violators(): Violator[] {
    if (this.args.violatorData) {
      const data = this.args.violatorData
        .map((violator) => {
          const roundedPercentages = roundParityPercentages(
            violator.data.calculatedValues,
          );
          const percentage = roundedPercentages[this.args.key];
          const numberOfShops = violator.data.value[this.args.key];
          const total = violator.data.calculatedValues.totalNbOfShops;

          return {
            name: violator.name,
            formattedPercentage: total
              ? this.intl.formatNumber(percentage, { style: 'percent' })
              : this.intl.t('general.missing'),
            formattedNumberOfShops: total
              ? numberOfShops.toString()
              : this.intl.t('general.missing'),
            numberOfShops,
            total,
            formattedTotal: total
              ? total.toString()
              : this.intl.t('general.missing'),
            backgroundColor: htmlSafe(`background-color: ${violator.color}`),
            borderColor: htmlSafe(`border-color: ${violator.color}`),
            selected: violator.selected,
            sortValue:
              this.args.sortType === SORT_TYPES.RELATIVE
                ? percentage
                : numberOfShops,
          };
        })
        .sort((a, b) => {
          const diff = b.sortValue - a.sortValue;

          if (diff === 0) {
            return b.total - a.total;
          }
          return diff;
        });

      return data;
    }

    return [];
  }

  <template>
    <div class="parity-trends-tooltip-container">
      <PrismTooltipHeader>
        {{@displayName}}
      </PrismTooltipHeader>
      <div class="table-header">
        <div class="row">
          <p class="text-200--regular market-name">
            {{this.segmentationName}}
          </p>
          <p class="text-200--regular searches">
            {{t (get PARITY_SHOPS_TRANSLATION_KEYS @key)}}
          </p>
          <p class="text-200--regular percentage">
            {{t (get PARITY_FILTER_TRANSLATION_KEYS @key)}}
            %
          </p>
          <p class="text-200--regular total">
            {{t "general.total_shops"}}
          </p>
        </div>
      </div>
      <div class="table-body">
        {{#each this.violators as |violator|}}
          <div
            class="row flex items-center {{if violator.selected 'selected'}}"
            style={{violator.borderColor}}
          >
            {{#if violator.backgroundColor}}
              <div
                class="identifier-bullet"
                style={{violator.backgroundColor}}
              ></div>
            {{/if}}

            <p class="text-200--regular market-name truncate">
              {{violator.name}}
            </p>
            <p class="text-200--regular searches truncate">
              {{violator.formattedNumberOfShops}}
            </p>
            <p class="text-200--regular percentage truncate">
              {{violator.formattedPercentage}}
            </p>
            <p class="text-200--regular total truncate">
              {{violator.formattedTotal}}
            </p>
          </div>
        {{/each}}
      </div>
    </div>
  </template>
}
